// EnvioDocumentosPortalProductorList.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { RefreshCw, X, Search, FileSpreadsheet, Eye, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Select } from "./components/ui/select";
import { Switch } from "./components/ui/switch";
import { DataTable } from "./components/ui/data-table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from "./components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./components/ui/alert-dialog";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token =
  "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const TIPO_OPCIONES = [
  { value: "", label: "Todos" },
  { value: "R", label: "RECEPCIÓN DE MATERIA PRIMA" },
  { value: "P", label: "ORDEN DE PACKING" },
  { value: "A", label: "ANÁLISIS DE CALIDAD" },
];

const ESTADO_OPCIONES = [
  { value: "", label: "Todos" },
  { value: "E", label: "ENVIADO" },
  { value: "P", label: "PENDIENTE" },
];

const mapTipoToText = (cod) => {
  if (cod === "P") return "ORDEN DE PACKING";
  if (cod === "R") return "RECEPCIÓN DE MATERIA PRIMA";
  if (cod === "A") return "ANÁLISIS DE CALIDAD";
  return cod ?? "";
};

const toFlatRow = (r) => ({
  ID: r.folio,
  Fecha: r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "",
  Productor: r.productor ?? "",
  "Guía Entrega": r.guiaentrega ?? "",
  "Tipo Documento": mapTipoToText(r.tipodocumento),
  Estado: String(r.estadocodigo).toUpperCase() === "E" ? "ENVIADO" : "PENDIENTE",
});

const EnvioDocumentosPortalProductorList = () => {
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [estadoEnvio, setEstadoEnvio] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");
  const [rowsRaw, setRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchTextDebounced(searchText.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchText]);

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map((r, i) => {
      const estadoTxt = String(r.estadocodigo).toUpperCase() === "E" ? "ENVIADO" : "PENDIENTE";
      const haystack = [r.folio, r.productor ?? "", r.guiaentrega ?? "", mapTipoToText(r.tipodocumento) ?? "", r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "", estadoTxt].join(" ").toLowerCase();
      return { key: r.folio ?? i, ...r, _haystack: haystack };
    });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetDocumentosPortalProductor`, {
        headers: { Authorization: token },
        params: { tipoDocumento: (tipoDocumento || "").trim(), estadoEnvio: (estadoEnvio || "").trim() },
      });
      const payload = response.data?.d ? JSON.parse(response.data.d) : response.data;
      setRowsRaw(normalize(payload));
      setPagination((p) => ({ ...p, current: 1 }));
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar el listado.");
    } finally {
      setLoading(false);
    }
  }, [tipoDocumento, estadoEnvio]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRows = useMemo(() => {
    if (!searchTextDebounced) return rowsRaw;
    return rowsRaw.filter((r) => r._haystack?.includes(searchTextDebounced));
  }, [rowsRaw, searchTextDebounced]);

  const handleToggleEstado = useCallback(async (folio, checked) => {
    const nuevoEstado = checked ? "E" : "P";
    try {
      const res = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/ActualizarEstadoDocumento`,
        JSON.stringify({ folio, nuevoEstado }),
        { headers: { "Content-Type": "application/json; charset=utf-8", Authorization: token, Accept: "application/json" } }
      );
      const d = res?.data?.d ?? "";
      if (typeof d === "string" && d.startsWith("ERROR")) { toast.error(d); fetchData(); return; }
      toast.success("Estado actualizado.");
      setRowsRaw((prev) => prev.map((r) => r.folio === folio ? { ...r, estadocodigo: nuevoEstado } : r));
    } catch (err) {
      console.error(err);
      toast.error("No se pudo actualizar el estado.");
      fetchData();
    }
  }, [fetchData]);

  const deleteRequest = async (folio) => {
    try {
      const res = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/EliminarDocumento`,
        JSON.stringify({ folio: String(folio) }),
        { headers: { "Content-Type": "application/json; charset=utf-8", Authorization: token, Accept: "application/json" }, validateStatus: () => true }
      );
      const body = res?.data?.d ?? res?.data ?? "";
      const text = typeof body === "string" ? body : JSON.stringify(body);
      if (res.status !== 200) { toast.error(text || `Error HTTP ${res.status}`); return false; }
      if (text.startsWith?.("ERROR")) { toast.error(text); return false; }
      toast.success("Documento eliminado correctamente.");
      setRowsRaw((prev) => prev.filter((r) => r.folio !== folio));
      return true;
    } catch (err) {
      console.error(err);
      toast.error("No se pudo eliminar el documento.");
      return false;
    }
  };

  const handleVerDocumento = useCallback(async (folio) => {
    try {
      const res = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetDocumentoPDF`, {
        params: { folio },
        headers: { Authorization: token },
        responseType: "blob",
        validateStatus: (s) => s < 500,
      });
      if (res.status !== 200) {
        const text = await res.data.text?.().catch(() => "");
        toast.error(text || (res.status === 404 ? "Archivo no encontrado." : "No se pudo cargar el documento."));
        return;
      }
      const ct = res.headers["content-type"] || res.data?.type || "";
      if (!ct.toLowerCase().includes("application/pdf")) {
        const text = await res.data.text?.().catch(() => "");
        toast.error(text || "La respuesta no es un PDF válido.");
        return;
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfVisible(true);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo cargar el documento.");
    }
  }, []);

  useEffect(() => {
    return () => { if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  const columns = useMemo(() => [
    { title: "ID", dataIndex: "folio", width: 90, sorter: (a, b) => (Number(a.folio) || 0) - (Number(b.folio) || 0), defaultSortOrder: "descend" },
    { title: "Fecha", dataIndex: "fecha", width: 130, render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : ""), sorter: (a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix() },
    { title: "Productor", dataIndex: "productor", ellipsis: true, width: 260, sorter: (a, b) => String(a.productor).localeCompare(String(b.productor)) },
    { title: "Guía Entrega", dataIndex: "guiaentrega", width: 150, sorter: (a, b) => String(a.guiaentrega).localeCompare(String(b.guiaentrega)) },
    { title: "Tipo Documento", dataIndex: "tipodocumento", width: 220, render: (cod) => mapTipoToText(cod) },
    {
      title: "Publicación",
      dataIndex: "estadocodigo",
      width: 140,
      render: (value, record) => (
        <Switch
          checked={String(value).toUpperCase() === "E"}
          onCheckedChange={(checked) => handleToggleEstado(record.folio, checked)}
        />
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 200,
      render: (_, r) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => handleVerDocumento(r.folio)}>
            <Eye size={14} /> Ver
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive">
                <Trash2 size={14} /> Eliminar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción eliminará el documento con ID {r.folio}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => deleteRequest(r.folio)}
                >
                  Sí, eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ),
    },
  ], [handleToggleEstado, handleVerDocumento]);

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = filteredRows.map(toFlatRow);
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Documentos");
      XLSX.writeFile(wb, `DocumentosPortalProductor_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
    } catch (e) { console.error(e); toast.error("No se pudo exportar a Excel."); }
  }, [filteredRows]);

  const handleLimpiar = () => {
    setTipoDocumento(""); setEstadoEnvio(""); setSearchText("");
    setPagination({ current: 1, pageSize: 20 });
    fetchData();
  };

  return (
    <div className="bg-white p-4 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Envíos de Documentos a Productores</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Buscar productor / guía / tipo documento / ID / fecha / estado"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-96"
            />
            {searchText && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <Select
            value={tipoDocumento}
            onChange={(v) => setTipoDocumento(v ?? "")}
            style={{ width: 280 }}
            placeholder="Tipo de Documento"
            options={TIPO_OPCIONES}
          />
          <Select
            value={estadoEnvio}
            onChange={(v) => setEstadoEnvio(v ?? "")}
            style={{ width: 180 }}
            placeholder="Estado de Envío"
            options={ESTADO_OPCIONES}
          />
          <Button onClick={fetchData} loading={loading}>
            <RefreshCw className="h-4 w-4" /> Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiar}>
            <X className="h-4 w-4" /> Limpiar
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={filteredRows}
        rowKey="folio"
        bordered
        scroll={{ x: 1100 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
        }}
        onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize })}
      />

      {/* Modal visor PDF */}
      <Dialog open={pdfVisible} onOpenChange={(open) => {
        if (!open && pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
        setPdfVisible(open);
        if (!open) setPdfUrl("");
      }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Ver Documento</DialogTitle>
          </DialogHeader>
          <DialogBody className="p-0">
            {pdfUrl ? (
              <iframe title="pdfViewer" src={pdfUrl} width="100%" height="620" className="border-0" />
            ) : (
              <div className="p-6 text-center text-gray-400">Cargando...</div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnvioDocumentosPortalProductorList;
